// @ts-nocheck
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import AdminTopNav from "./AdminTopNav";
import "./AdminBrandMyLaptop.css";

interface Slot {
    slotId: string;
    section: "lid" | "palm_left" | "palm_right";
    name: string;
    dimensions: string;
    price: number;
    currency: string;
    isBooked: boolean;
    sponsor?: {
        companyName?: string;
        website?: string;
        logoUrl?: string;
        bookedAt?: string;
        contactEmail?: string;
    };
}

interface Campaign {
    _id: string;
    title: string;
    description: string;
    destination: string;
    startDate?: string;
    endDate?: string;
    bookingOpenDate?: string;
    bookingCloseDate?: string;
    status: "open" | "extended" | "closed" | "completed";
    isExtended: boolean;
    slots: Slot[];
    proofOfWork?: {
        photos: string[];
        recapText: string;
        publishedAt?: string;
    };
    createdAt: string;
}

export default function AdminBrandMyLaptop() {
    const [navPinned, setNavPinned] = useState(false);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);

    // Modal state for creating a new weekly campaign
    const [showNewModal, setShowNewModal] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newDestination, setNewDestination] = useState("");
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [newBookingCloseDate, setNewBookingCloseDate] = useState("");

    // Proof-of-work state
    const [showProofModal, setShowProofModal] = useState(false);
    const [proofRecap, setProofRecap] = useState("");
    const [proofFiles, setProofFiles] = useState<FileList | null>(null);
    const [submittingProof, setSubmittingProof] = useState(false);

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGNS);
            if (res.data && res.data.success) {
                setCampaigns(res.data.campaigns || []);
                const current = res.data.campaigns.find(
                    (c: Campaign) => c.status === "open" || c.status === "extended"
                ) || res.data.campaigns[0];
                setActiveCampaign(current || null);
            }
        } catch (err) {
            console.error("Error fetching campaigns:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    // Toggle deadline extension
    const handleToggleExtension = async () => {
        if (!activeCampaign) return;
        const newExtended = !activeCampaign.isExtended;
        const newStatus = newExtended ? "extended" : "open";

        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(activeCampaign._id),
                {
                    isExtended: newExtended,
                    status: newStatus
                }
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error extending campaign:", err);
        }
    };

    // Close booking for Saturday
    const handleCloseBooking = async () => {
        if (!activeCampaign) return;
        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(activeCampaign._id),
                {
                    status: "closed",
                    isExtended: false
                }
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error closing booking:", err);
        }
    };

    // Reset a slot if requested
    const handleResetSlot = async (slotId: string) => {
        if (!activeCampaign) return;
        if (!window.confirm(`Are you sure you want to reset and free up slot ${slotId}?`)) return;

        try {
            const res = await axios.post(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_SLOT_RESET(activeCampaign._id, slotId),
                {}
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error resetting slot:", err);
        }
    };

    // Create New Weekly Campaign
    const handleCreateCampaign = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await axios.post(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_CREATE, {
                title: newTitle,
                description: newDesc,
                destination: newDestination,
                startDate: newStartDate,
                endDate: newEndDate,
                bookingCloseDate: newBookingCloseDate,
                makeActive: true
            });

            if (res.data && res.data.success) {
                setShowNewModal(false);
                setNewTitle("");
                setNewDesc("");
                setNewDestination("");
                setNewStartDate("");
                setNewEndDate("");
                setNewBookingCloseDate("");
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error creating campaign:", err);
        }
    };

    // Submit Proof of Work (Trip Photos with stickered laptop)
    const handleSubmitProof = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeCampaign) return;
        setSubmittingProof(true);

        try {
            const formData = new FormData();
            formData.append("recapText", proofRecap);
            formData.append("markCompleted", "true");
            if (proofFiles) {
                for (let i = 0; i < proofFiles.length; i++) {
                    formData.append("photos", proofFiles[i]);
                }
            }

            const res = await axios.post(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_PROOF(activeCampaign._id),
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (res.data && res.data.success) {
                setShowProofModal(false);
                setProofRecap("");
                setProofFiles(null);
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error uploading proof of work:", err);
        } finally {
            setSubmittingProof(false);
        }
    };

    const bookedSlots = activeCampaign?.slots.filter((s) => s.isBooked) || [];

    return (
        <div className="admin-bml-container">
            <AdminTopNav
                activeTab="brandmylaptop"
                navPinned={navPinned}
                onTogglePin={() => setNavPinned(!navPinned)}
            />

            <div className="admin-bml-header">
                <div>
                    <h1>Brand My Laptop • Weekly Event Manager</h1>
                    <p>
                        Manage Apple MacBook Pro 14" Silver surface sponsorship bookings, weekly Mon–Fri event cycles, and post completed trip updates.
                    </p>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                    <button
                        className="admin-bml-btn primary"
                        onClick={() => setShowNewModal(true)}
                    >
                        <i className="fi fi-rr-plus"></i> New Weekly Event
                    </button>
                    <a
                        href="/brand-my-laptop"
                        target="_blank"
                        rel="noreferrer"
                        className="admin-bml-btn secondary"
                    >
                        <i className="fi fi-rr-eye"></i> View Public Page
                    </a>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                    Loading campaigns...
                </div>
            ) : activeCampaign ? (
                <>
                    {/* Active Campaign Card */}
                    <div className="admin-bml-card">
                        <h2>
                            <i className="fi fi-rr-laptop" style={{ color: "#2563eb" }}></i>
                            Current Campaign: {activeCampaign.title}
                        </h2>

                        <div className="admin-campaign-summary">
                            <div className="admin-summary-item">
                                <span className="label">Destination / Event</span>
                                <span className="value">{activeCampaign.destination}</span>
                            </div>
                            <div className="admin-summary-item">
                                <span className="label">Status</span>
                                <span className="value" style={{ textTransform: "uppercase", color: activeCampaign.status === "open" ? "#10b981" : "#f59e0b" }}>
                                    {activeCampaign.status} {activeCampaign.isExtended ? "(Extended)" : ""}
                                </span>
                            </div>
                            <div className="admin-summary-item">
                                <span className="label">Booked Slots</span>
                                <span className="value">
                                    {bookedSlots.length} / {activeCampaign.slots.length} Slots
                                </span>
                            </div>
                            <div className="admin-summary-item">
                                <span className="label">Total Revenue</span>
                                <span className="value" style={{ color: "#10b981" }}>
                                    ${bookedSlots.reduce((acc, s) => acc + (s.price || 0), 0)}
                                </span>
                            </div>
                        </div>

                        {/* Action controls */}
                        <div className="admin-campaign-actions">
                            <button
                                className={`admin-bml-btn ${activeCampaign.isExtended ? "warning" : "secondary"}`}
                                onClick={handleToggleExtension}
                            >
                                <i className="fi fi-rr-clock"></i>
                                {activeCampaign.isExtended ? "Cancel Deadline Extension" : "Extend Booking (Beyond Saturday)"}
                            </button>

                            {activeCampaign.status !== "closed" && (
                                <button
                                    className="admin-bml-btn secondary"
                                    onClick={handleCloseBooking}
                                >
                                    <i className="fi fi-rr-lock"></i> Close Booking Now
                                </button>
                            )}

                            <button
                                className="admin-bml-btn success"
                                onClick={() => {
                                    setProofRecap(activeCampaign.proofOfWork?.recapText || "");
                                    setShowProofModal(true);
                                }}
                            >
                                <i className="fi fi-rr-camera"></i> Post Trip Proof & Photos (Mark Done)
                            </button>
                        </div>
                    </div>

                    {/* Slots Management Table */}
                    <div className="admin-bml-card">
                        <h2>
                            <i className="fi fi-rr-apps" style={{ color: "#2563eb" }}></i>
                            All 16 MacBook Pro Surface Slots
                        </h2>

                        <table className="admin-slots-table">
                            <thead>
                                <tr>
                                    <th>Slot ID</th>
                                    <th>Section</th>
                                    <th>Slot Name</th>
                                    <th>Dimensions</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                    <th>Sponsor Details</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeCampaign.slots.map((slot) => (
                                    <tr key={slot.slotId}>
                                        <td><strong>{slot.slotId}</strong></td>
                                        <td style={{ textTransform: "capitalize" }}>{slot.section.replace("_", " ")}</td>
                                        <td>{slot.name}</td>
                                        <td>{slot.dimensions}</td>
                                        <td><strong>${slot.price}</strong></td>
                                        <td>
                                            {slot.isBooked ? (
                                                <span style={{ color: "#10b981", fontWeight: 700 }}>● BOOKED</span>
                                            ) : (
                                                <span style={{ color: "#64748b" }}>Available</span>
                                            )}
                                        </td>
                                        <td>
                                            {slot.isBooked ? (
                                                <div className="admin-sponsor-preview-cell">
                                                    {slot.sponsor?.logoUrl && (
                                                        <img
                                                            src={slot.sponsor.logoUrl.startsWith("http") ? slot.sponsor.logoUrl : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${slot.sponsor.logoUrl}`}
                                                            alt=""
                                                        />
                                                    )}
                                                    <div>
                                                        <strong>{slot.sponsor?.companyName}</strong>
                                                        <div style={{ fontSize: 11, color: "#2563eb" }}>
                                                            <a href={slot.sponsor?.website} target="_blank" rel="noreferrer">
                                                                {slot.sponsor?.website}
                                                            </a>
                                                        </div>
                                                        {slot.sponsor?.contactEmail && (
                                                            <div style={{ fontSize: 11, color: "#64748b" }}>
                                                                {slot.sponsor.contactEmail}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={{ color: "#94a3b8" }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            {slot.isBooked && (
                                                <button
                                                    className="admin-bml-btn secondary"
                                                    style={{ padding: "4px 10px", fontSize: 12 }}
                                                    onClick={() => handleResetSlot(slot.slotId)}
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <div style={{ padding: 40, textAlign: "center", background: "#f8fafc", borderRadius: 16 }}>
                    <h3>No Active Weekly Campaign</h3>
                    <p>Click "New Weekly Event" above to create this week's booking campaign!</p>
                </div>
            )}

            {/* Create Weekly Event Modal */}
            {showNewModal && (
                <div className="admin-modal-overlay" onClick={() => setShowNewModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Launch New Weekly Event</h2>
                        <form onSubmit={handleCreateCampaign}>
                            <div className="admin-form-group">
                                <label>Event / Trip Title *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. National Tech Conference 2026"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Destination Venue / City *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Bengaluru Tech Expo"
                                    value={newDestination}
                                    onChange={(e) => setNewDestination(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Trip Description / Event Pitch</label>
                                <textarea
                                    rows={3}
                                    placeholder="Tell sponsors where you will be carrying the laptop and why they should book..."
                                    value={newDesc}
                                    onChange={(e) => setNewDesc(e.target.value)}
                                />
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div className="admin-form-group">
                                    <label>Trip Start Date</label>
                                    <input
                                        type="date"
                                        value={newStartDate}
                                        onChange={(e) => setNewStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Trip End Date</label>
                                    <input
                                        type="date"
                                        value={newEndDate}
                                        onChange={(e) => setNewEndDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="admin-form-group">
                                <label>Booking Cutoff Date (Defaults to Saturday)</label>
                                <input
                                    type="date"
                                    value={newBookingCloseDate}
                                    onChange={(e) => setNewBookingCloseDate(e.target.value)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowNewModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="admin-bml-btn primary">
                                    Launch Weekly Event
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Proof of Work Modal */}
            {showProofModal && (
                <div className="admin-modal-overlay" onClick={() => setShowProofModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Post Trip Proof & Branded Laptop Photos</h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Upload real photos of your MacBook Pro stickered with sponsor brands from the trip. This will be published to the public history archive!
                        </p>

                        <form onSubmit={handleSubmitProof}>
                            <div className="admin-form-group">
                                <label>Trip Recap & Highlights</label>
                                <textarea
                                    rows={4}
                                    placeholder="Write a recap of your trip, founder meetings, demos given on the laptop..."
                                    value={proofRecap}
                                    onChange={(e) => setProofRecap(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Upload Trip Photos with Laptop (up to 5 photos)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setProofFiles(e.target.files)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowProofModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="admin-bml-btn success"
                                    disabled={submittingProof}
                                >
                                    {submittingProof ? "Uploading & Publishing..." : "Publish to History"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
